/**
 * ScheduleRow — compact inline editor for one relay schedule slot.
 *
 * Used by RelayBlade to show enabled schedules already in edit mode, while
 * disabled schedules remain enable-first rows. Preserves the existing Formik
 * field contract for day masks, sun offsets, always-on, dimming, and timing.
 *
 * Contains:
 * - ScheduleRow — the schedule editor row (default export)
 */
import {
  Box,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Slider,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Field } from 'formik';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useUser } from '@sophocles/app';
import DayOfWeekField from '@sophocles/components/DayOfWeekField';
import { scheduleStart, scheduleDays, scheduleEnd } from '@sophocles/entities/RelaySchedule';
import { getTimeFormat } from '@sophocles/utils/formatUtils';
import { dimmingData } from '@sophocles/views/sites/components/RelaySchedulesForm';
import { SmallTimePicker, deriveDirection } from '@sophocles/views/sites/components/relayScheduleFields';

const scheduleCardSx = {
  mx: 'var(--space-3)',
  my: 'var(--space-2)',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-md)',
  bgcolor: 'background.paper',
  overflow: 'hidden',
};

const scheduleHeaderSx = {
  px: 'var(--space-3)',
  pt: 'var(--space-2)',
  pb: 'var(--space-1)',
  bgcolor: 'transparent',
};

const scheduleBodySx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-2)',
  px: 'var(--space-3)',
  pb: 'var(--space-3)',
  pt: 'var(--space-1)',
};

const controlPanelSx = {
  minWidth: 0,
};

const timeGridSx = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(2, minmax(0, 1fr))',
  },
  gap: 'var(--space-3)',
};

const toggleGroupSx = {
  width: '100%',
  '& .MuiToggleButton-root': {
    flex: 1,
    minWidth: 0,
    px: 'var(--space-2)',
    py: 'var(--space-1)',
  },
};

const DEFAULT_SUN_OFFSET_MINUTES = 30;

function getDirectionLabel(direction, t) {
  if (direction === 'before') return t('general.before', 'Before');
  if (direction === 'after') return t('general.after', 'After');
  return t('general.at', 'At');
}

function getDaysText(schedule, t) {
  if (Array.isArray(schedule.daysOfWeek) && schedule.daysOfWeek.length === 0) {
    return t('general.noDaysSelected', 'No days selected');
  }

  const days = scheduleDays(schedule.daysOfWeek, t);
  if (days.length === 7) {
    return t('general.everyDay', 'Every Day');
  }
  return days.join(' ');
}

function getScheduleSummary({ dimmerAvailable, nextSunrise, nextSunset, schedule, supportsDayOfWeek, t, userJs }) {
  if (!schedule.enabled) {
    return t('sites.inactiveSchedule', 'Disabled');
  }

  if (schedule.isAlwaysOn) {
    return dimmerAvailable
      ? `${t('sites.alwaysOn', 'Always On')} / ${dimmingData(schedule, t)}`
      : t('sites.alwaysOn', 'Always On');
  }

  const onText = scheduleStart(
    schedule.isAlwaysOn,
    schedule.onTime,
    schedule.onType,
    schedule.onOffset,
    nextSunrise,
    nextSunset,
    userJs,
    t,
  );
  const offText = scheduleEnd(
    schedule.isAlwaysOn,
    schedule.offTime,
    schedule.offType,
    schedule.offOffset,
    nextSunrise,
    nextSunset,
    userJs,
    t,
  );

  const summaryParts = [];
  if (supportsDayOfWeek) {
    summaryParts.push(getDaysText(schedule, t));
  }
  summaryParts.push(`${t('sites.on', 'On')}: ${onText}`);
  summaryParts.push(`${t('sites.off', 'Off')}: ${offText}`);
  if (dimmerAvailable) {
    summaryParts.push(dimmingData(schedule, t));
  }

  return summaryParts.join(' / ');
}

function SunOffsetField({ field, form, meta, eventLabel, fixedDirection = null, ariaLabel }) {
  const { t } = useTranslation();
  const direction = fixedDirection ?? deriveDirection(field.value);
  const offsetMagnitude = Math.abs(Number(field.value) || 0);

  const handleDirectionChange = event => {
    const newValue = event.target.value;
    if (newValue === 'at') {
      form.setFieldValue(field.name, 0);
      return;
    }
    const magnitude = offsetMagnitude || DEFAULT_SUN_OFFSET_MINUTES;
    form.setFieldValue(field.name, newValue === 'before' ? -magnitude : magnitude);
  };

  const handleValueChange = event => {
    const raw = parseInt(event.target.value, 10);
    const magnitude = Number.isFinite(raw) ? Math.abs(raw) : 0;
    form.setFieldValue(field.name, direction === 'before' ? -magnitude : magnitude);
  };

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
      {direction !== 'at' && (
        <TextField
          {...field}
          size="small"
          type="number"
          label={t('sites.offsetMinutes', 'Minutes')}
          value={offsetMagnitude}
          error={meta.touched && Boolean(meta.error)}
          helperText={meta.touched && meta.error}
          onChange={handleValueChange}
          InputProps={{ endAdornment: <InputAdornment position="end">min</InputAdornment> }}
          sx={{ width: { sm: 132 } }}
        />
      )}
      {fixedDirection ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: { sm: 0.5 }, whiteSpace: 'nowrap' }}>
          {getDirectionLabel(direction, t).toLowerCase()}
        </Typography>
      ) : (
        <TextField
          select
          size="small"
          label={t('sites.sunTiming', 'Timing')}
          value={direction}
          onChange={handleDirectionChange}
          aria-label={ariaLabel}
          sx={{ minWidth: { sm: 128 } }}
        >
          <MenuItem value="at">{t('general.at', 'At')}</MenuItem>
          <MenuItem value="before">{t('general.before', 'Before')}</MenuItem>
          <MenuItem value="after">{t('general.after', 'After')}</MenuItem>
        </TextField>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
        {eventLabel}
      </Typography>
    </Stack>
  );
}

function TypeToggleField({ name, options, ariaLabel }) {
  return (
    <Field name={name}>
      {({ field, form }) => (
        <ToggleButtonGroup
          exclusive
          color="primary"
          size="small"
          value={field.value}
          onChange={(_event, newValue) => {
            if (newValue !== null) {
              form.setFieldValue(field.name, newValue);
            }
          }}
          aria-label={ariaLabel}
          sx={toggleGroupSx}
        >
          {options.map(option => (
            <ToggleButton value={option.value} key={option.value}>
              <span>{option.label}</span>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}
    </Field>
  );
}

function TimeBlock({
  label,
  typeValue,
  typeField,
  timeField,
  offsetField,
  options,
  supportsNegativeOffsets,
  getFieldName,
  userJs,
}) {
  const { t } = useTranslation();
  const eventLabel = options.find(option => option.value === typeValue)?.label ?? typeValue;
  const fixedDirection = supportsNegativeOffsets ? null : typeValue === 'sunset' ? 'before' : 'after';

  return (
    <Stack spacing={1} sx={controlPanelSx}>
      <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <TypeToggleField name={getFieldName(typeField)} options={options} ariaLabel={`${label} type`} />

      {typeValue !== 'time' ? (
        <Field name={getFieldName(offsetField)}>
          {({ field, form, meta }) =>
            supportsNegativeOffsets ? (
              <SunOffsetField
                field={field}
                form={form}
                meta={meta}
                eventLabel={eventLabel}
                ariaLabel={`${label} ${t('sites.offsetDirection', 'Offset Direction')}`}
              />
            ) : (
              <SunOffsetField
                field={field}
                form={form}
                meta={meta}
                eventLabel={eventLabel}
                fixedDirection={fixedDirection}
                ariaLabel={`${label} ${t('sites.offsetDirection', 'Offset Direction')}`}
              />
            )
          }
        </Field>
      ) : (
        <Field name={getFieldName(timeField)}>
          {({ field, form }) => (
            <SmallTimePicker
              format={getTimeFormat(userJs)}
              value={field.value}
              onChange={time => {
                form.setFieldValue(field.name, time || null);
              }}
              slotProps={{ textField: { label, fullWidth: true, size: 'small' } }}
            />
          )}
        </Field>
      )}
    </Stack>
  );
}

function DimmerBlock({ schedule, fieldPrefix, is2PDC }) {
  const { t } = useTranslation();

  return (
    <Stack spacing={1} sx={controlPanelSx}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
        <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
          {t('sites.brightness', 'Brightness')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {dimmingData(schedule, t)}
        </Typography>
      </Stack>
      <Field name={`${fieldPrefix}.dutyCycle`} type="number">
        {({ field: { value }, form }) => (
          <Slider
            value={value}
            min={0}
            max={100}
            step={5}
            onChange={(_event, newValue) => form.setFieldValue(`${fieldPrefix}.dutyCycle`, newValue)}
          />
        )}
      </Field>
      {is2PDC && (
        <Field name={`${fieldPrefix}.frequency`}>
          {({ field, meta }) => (
            <TextField
              {...field}
              fullWidth
              size="small"
              type="number"
              label={t('sites.dimmingFrequency', 'Dimming Frequency (Hz)')}
              error={meta.touched && Boolean(meta.error)}
              helperText={meta.touched && meta.error}
            />
          )}
        </Field>
      )}
    </Stack>
  );
}

export default function ScheduleRow({
  title,
  schedule,
  namePath,
  dimmerAvailable = false,
  is2PDC = false,
  supportsDayOfWeek = true,
  supportsNegativeOffsets = false,
  showAlwaysOn = false,
  collapsed = false,
  hideIsActive = false,
  nextSunset = null,
  nextSunrise = null,
}) {
  const { t } = useTranslation();
  const { user } = useUser();
  const userJs = user.toJS();

  const getFieldName = field => `${namePath}.${field}`;
  const summaryText = getScheduleSummary({
    dimmerAvailable,
    nextSunrise,
    nextSunset,
    schedule,
    supportsDayOfWeek,
    t,
    userJs,
  });

  const onOptions = [
    { value: 'time', label: t('general.time', 'Time') },
    { value: 'sunset', label: t('sites.sunset', 'Sunset') },
    ...(supportsNegativeOffsets ? [{ value: 'sunrise', label: t('sites.sunrise', 'Sunrise') }] : []),
  ];
  const offOptions = [
    { value: 'time', label: t('general.time', 'Time') },
    ...(supportsNegativeOffsets ? [{ value: 'sunset', label: t('sites.sunset', 'Sunset') }] : []),
    { value: 'sunrise', label: t('sites.sunrise', 'Sunrise') },
  ];

  return (
    <Box sx={scheduleCardSx}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={scheduleHeaderSx}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" color={schedule.enabled ? 'text.primary' : 'text.secondary'}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {summaryText}
          </Typography>
        </Box>
        {!hideIsActive && (
          <Field name={getFieldName('enabled')}>
            {({ field }) => (
              <FormControlLabel
                label={field.value ? t('general.enabled', 'Enabled') : t('general.disabled', 'Disabled')}
                labelPlacement="start"
                sx={{ m: 0, justifyContent: { xs: 'space-between', sm: 'flex-end' } }}
                control={
                  <Switch
                    {...field}
                    checked={Boolean(field.value)}
                    onChange={event => field.onChange(event)}
                    sx={{ ml: 1 }}
                  />
                }
              />
            )}
          </Field>
        )}
      </Stack>

      {!collapsed && !schedule.enabled && (
        <Box sx={scheduleBodySx}>
          <Typography variant="body2" color="text.secondary">
            {dimmerAvailable
              ? t('sites.enableScheduleToEdit', 'Enable this schedule to edit timing and dimming.')
              : t('sites.enableScheduleToEditTiming', 'Enable this schedule to edit timing.')}
          </Typography>
        </Box>
      )}

      {!collapsed && schedule.enabled && (
        <Box sx={scheduleBodySx}>
          {showAlwaysOn && (
            <Field name={getFieldName('isAlwaysOn')}>
              {({ field }) => (
                <FormControlLabel
                  label={t('sites.alwaysOn', 'Always On')}
                  sx={{ m: 0 }}
                  control={
                    <Switch
                      {...field}
                      checked={Boolean(field.value)}
                      onChange={event => field.onChange(event)}
                      sx={{ mr: 1 }}
                    />
                  }
                />
              )}
            </Field>
          )}

          {!schedule.isAlwaysOn && supportsDayOfWeek && (
            <DayOfWeekField
              required
              fullWidth
              label={t('sites.daysOfWeek', 'Days of Week')}
              name={getFieldName('daysOfWeek')}
              size="small"
            />
          )}

          {!schedule.isAlwaysOn && (
            <Box sx={timeGridSx}>
              <TimeBlock
                label={t('sites.on', 'On')}
                typeValue={schedule.onType}
                typeField="onType"
                timeField="onTime"
                offsetField="onOffset"
                options={onOptions}
                supportsNegativeOffsets={supportsNegativeOffsets}
                getFieldName={getFieldName}
                userJs={userJs}
              />
              <TimeBlock
                label={t('sites.off', 'Off')}
                typeValue={schedule.offType}
                typeField="offType"
                timeField="offTime"
                offsetField="offOffset"
                options={offOptions}
                supportsNegativeOffsets={supportsNegativeOffsets}
                getFieldName={getFieldName}
                userJs={userJs}
              />
            </Box>
          )}

          {dimmerAvailable && <DimmerBlock schedule={schedule} fieldPrefix={namePath} is2PDC={is2PDC} />}
        </Box>
      )}
    </Box>
  );
}

SunOffsetField.propTypes = {
  field: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
  eventLabel: PropTypes.string.isRequired,
  fixedDirection: PropTypes.oneOf(['before', 'after']),
  ariaLabel: PropTypes.string.isRequired,
};

TypeToggleField.propTypes = {
  name: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  ariaLabel: PropTypes.string.isRequired,
};

TimeBlock.propTypes = {
  label: PropTypes.string.isRequired,
  typeValue: PropTypes.string.isRequired,
  typeField: PropTypes.string.isRequired,
  timeField: PropTypes.string.isRequired,
  offsetField: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  supportsNegativeOffsets: PropTypes.bool.isRequired,
  getFieldName: PropTypes.func.isRequired,
  userJs: PropTypes.object.isRequired,
};

DimmerBlock.propTypes = {
  schedule: PropTypes.object.isRequired,
  fieldPrefix: PropTypes.string.isRequired,
  is2PDC: PropTypes.bool.isRequired,
};

ScheduleRow.propTypes = {
  title: PropTypes.string.isRequired,
  schedule: PropTypes.object.isRequired,
  namePath: PropTypes.string.isRequired,
  dimmerAvailable: PropTypes.bool,
  is2PDC: PropTypes.bool,
  supportsDayOfWeek: PropTypes.bool,
  supportsNegativeOffsets: PropTypes.bool,
  showAlwaysOn: PropTypes.bool,
  collapsed: PropTypes.bool,
  hideIsActive: PropTypes.bool,
  nextSunset: PropTypes.instanceOf(Date),
  nextSunrise: PropTypes.instanceOf(Date),
};
